import { Search } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";

import { RepackingDesktopTable } from "./repacking-desktop-table";
import { RepackingMobileList } from "./repacking-mobile-list";
import { filterRepackings } from "./repacking-utils";

import type { StockTransformation } from "@artisancode/api-types";

interface Props {
  transformations: StockTransformation[];
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  onViewDetail: (trans: StockTransformation) => void;
  onComplete: (id: string) => void;
  onCancelRequest: (trans: StockTransformation) => void;
  isCompleting?: boolean;
}

export function RepackingTableCard({
  transformations,
  searchQuery,
  onSearchQueryChange,
  statusFilter,
  onStatusFilterChange,
  onViewDetail,
  onComplete,
  onCancelRequest,
  isCompleting,
}: Props) {
  const isMobile = useIsMobile();
  const canComplete = useHasPermission("stock_transformations.complete");
  const filtered = filterRepackings(transformations, searchQuery, statusFilter);
  const rowsProps = {
    rows: filtered,
    canComplete,
    isCompleting,
    onViewDetail,
    onComplete,
    onCancelRequest,
  };

  return (
    <Card className="shadow-xs">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold">
            Riwayat Dokumen Repacking
          </CardTitle>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cari kode, bahan baku, catatan..."
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                className="h-9 pl-8 text-xs"
              />
            </div>

            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="h-9 w-full sm:w-[130px] text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Semua Status
                </SelectItem>
                <SelectItem value="draft" className="text-xs">
                  Draft
                </SelectItem>
                <SelectItem value="completed" className="text-xs">
                  Selesai
                </SelectItem>
                <SelectItem value="cancelled" className="text-xs">
                  Dibatalkan
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isMobile ? (
          <RepackingMobileList {...rowsProps} />
        ) : (
          <RepackingDesktopTable {...rowsProps} />
        )}
      </CardContent>
    </Card>
  );
}
